import { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, 
  Sparkles,
  CheckCircle2,
  XCircle,
  Loader2,
  Shield,
  Heart,
  BookOpen,
  Users,
  Brain,
  Calendar,
  MessageCircle,
  Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const freeTierFeatures = [
  { text: "Daily Crown Declaration", included: true, icon: Calendar },
  { text: "1 Educational intro lesson per module", included: true, icon: BookOpen },
  { text: "Basic no-contact day counter", included: true, icon: Flame },
  { text: "View community prayer wall", included: true, icon: Users },
  { text: "AI 'Adjust My Crown' support tool", included: false, icon: Crown },
  { text: "Full healing modules (20 lessons)", included: false, icon: BookOpen },
  { text: "Urge logging & milestone tracking", included: false, icon: Heart },
  { text: "Post prayer requests", included: false, icon: MessageCircle },
  { text: "Boundary script templates", included: false, icon: Shield },
  { text: "Nervous system exercises", included: false, icon: Brain },
];

const premiumFeatures = [
  { text: "Daily Crown Declaration", included: true, icon: Calendar },
  { text: "1 Educational intro lesson per module", included: true, icon: BookOpen },
  { text: "Basic no-contact day counter", included: true, icon: Flame },
  { text: "View community prayer wall", included: true, icon: Users },
  { text: "AI 'Adjust My Crown' support tool", included: true, icon: Crown, highlight: true },
  { text: "Full healing modules (20 lessons)", included: true, icon: BookOpen, highlight: true },
  { text: "Urge logging & milestone tracking", included: true, icon: Heart, highlight: true },
  { text: "Post prayer requests", included: true, icon: MessageCircle, highlight: true },
  { text: "Boundary script templates", included: true, icon: Shield, highlight: true },
  { text: "Nervous system exercises", included: true, icon: Brain, highlight: true },
];

export default function Subscription() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (user?.is_premium) {
    return (
      <Layout>
        <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="subscription-page">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full gold-gradient mb-6 animate-pulse-gold">
              <Crown className="w-12 h-12 text-purple-950" />
            </div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-4">
              You're a Crown Premium Member!
            </h1>
            <p className="text-muted-foreground mb-8">
              Thank you for investing in your healing journey. All features are unlocked.
            </p>
            
            <div className="crown-card p-6 text-left">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Your Premium Benefits
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {premiumFeatures.filter(f => f.highlight).map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-foreground">{feature.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Upgrade Your Healing
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Choose Your Path
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Start free or unlock the full recovery experience with Crown Premium.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Tier */}
          <div className="crown-card p-8">
            <div className="text-center mb-6">
              <h3 className="font-serif text-2xl font-bold text-foreground mb-2">Free</h3>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-foreground">$0</span>
                <span className="text-muted-foreground">/forever</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Your current plan</p>
            </div>

            <div className="space-y-3 mb-8">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">What's included:</p>
              {freeTierFeatures.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    {feature.included ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-muted-foreground/30 flex-shrink-0" />
                    )}
                    <Icon className={`w-4 h-4 flex-shrink-0 ${feature.included ? 'text-primary' : 'text-muted-foreground/30'}`} />
                    <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50 line-through'}>
                      {feature.text}
                    </span>
                  </div>
                );
              })}
            </div>

            <Button 
              disabled
              variant="outline"
              className="w-full rounded-full py-5 border-2"
            >
              Current Plan
            </Button>
          </div>

          {/* Premium Tier */}
          <div className="crown-card p-8 border-2 border-amber-400/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-2 gold-gradient"></div>
            <div className="absolute top-4 right-4">
              <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold">
                RECOMMENDED
              </span>
            </div>
            
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full gold-gradient mb-3">
                <Crown className="w-6 h-6 text-purple-950" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-foreground mb-2">Crown Premium</h3>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-foreground">$27</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Full access to all healing tools</p>
            </div>

            <div className="space-y-3 mb-8">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Everything in Free, plus:</p>
              {premiumFeatures.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className={`flex items-center gap-3 text-sm ${feature.highlight ? 'font-medium' : ''}`}>
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${feature.highlight ? 'text-amber-500' : 'text-green-500'}`} />
                    <Icon className={`w-4 h-4 flex-shrink-0 ${feature.highlight ? 'text-amber-600' : 'text-primary'}`} />
                    <span className="text-foreground">
                      {feature.text}
                      {feature.highlight && <Sparkles className="w-3 h-3 inline ml-1 text-amber-500" />}
                    </span>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={handleSubscribe}
              disabled={loading}
              data-testid="subscribe-btn"
              className="w-full btn-gold rounded-full py-5 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Please wait...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  Upgrade to Premium
                </>
              )}
            </Button>

            <p className="text-center text-xs text-muted-foreground mt-4">
              Cancel anytime. 7-day money-back guarantee.
            </p>
          </div>
        </div>

        {/* Testimonial */}
        <div className="mt-12 text-center max-w-xl mx-auto">
          <blockquote className="font-scripture text-lg italic text-foreground mb-4">
            "The premium content helped me understand patterns I didn't even know existed. 
            Worth every penny for my peace of mind."
          </blockquote>
          <p className="text-sm text-muted-foreground">— Sarah, Crown Premium Member</p>
        </div>

        {/* Money Back Guarantee */}
        <div className="mt-10 crown-card p-6 text-center max-w-lg mx-auto">
          <Shield className="w-8 h-8 text-primary mx-auto mb-3" />
          <h3 className="font-semibold text-foreground mb-2">Your Healing Matters to Us</h3>
          <p className="text-sm text-muted-foreground">
            If you're not satisfied within the first 7 days, we'll refund your payment. 
            No questions asked. We want you to heal, not stress about money.
          </p>
        </div>
      </div>
    </Layout>
  );
}
